import { Link } from "@tanstack/react-router";
import {
  BookMarked,
  BookOpen,
  GraduationCap,
  Lightbulb,
  type LucideIcon,
  PenTool,
  Rocket,
  Sparkles,
  Star,
} from "lucide-react";
import { ROUTES } from "#/lib/routes";
import { SUPPLIER_LOGOS } from "#/lib/supplier-logos";
import { useReveal } from "#/lib/useReveal";

type LocalBubble = {
  name: string;
  Icon: LucideIcon;
  logoSrc?: string;
  dark?: boolean;
  publisher: string;
};

type RowAnchor = "left" | "right";

const LOCAL_PARTNER_ROWS: ReadonlyArray<{
  anchor: RowAnchor;
  bubbles: ReadonlyArray<LocalBubble>;
}> = [
  {
    anchor: "right",
    bubbles: [
      {
        name: "Anvil Publishing",
        Icon: BookOpen,
        logoSrc: SUPPLIER_LOGOS.anvilPublishing,
        publisher: "anvil-publishing",
      },
      {
        name: "Brilliant Creations",
        Icon: Sparkles,
        logoSrc: SUPPLIER_LOGOS.brilliantCreations,
        publisher: "brilliant-creations",
      },
      {
        name: "C-E Publishing",
        Icon: PenTool,
        logoSrc: SUPPLIER_LOGOS.cePublishing,
        publisher: "c-e-publishing",
      },
      {
        name: "DIWA",
        Icon: GraduationCap,
        logoSrc: SUPPLIER_LOGOS.diwa,
        publisher: "diwa-learning-systems-inc",
      },
    ],
  },
  {
    anchor: "left",
    bubbles: [
      {
        name: "ICI Philippines",
        Icon: Star,
        logoSrc: SUPPLIER_LOGOS.iciPhilippines,
        publisher: "ici-ministries",
      },
      {
        name: "JO-ES",
        Icon: BookMarked,
        logoSrc: SUPPLIER_LOGOS.joes,
        publisher: "jo-es-publishing-house",
      },
      {
        name: "Kidstarter",
        Icon: Rocket,
        dark: true,
        logoSrc: SUPPLIER_LOGOS.kidstarter,
        publisher: "kidstarter",
      },
      {
        name: "OMF Lit",
        Icon: BookOpen,
        logoSrc: SUPPLIER_LOGOS.omfLit,
        publisher: "omf",
      },
    ],
  },
  {
    anchor: "right",
    bubbles: [
      {
        name: "REX Education",
        Icon: GraduationCap,
        logoSrc: SUPPLIER_LOGOS.rexEducation,
        publisher: "rex-bookstore",
      },
      {
        name: "SC Mardison",
        Icon: Lightbulb,
        logoSrc: SUPPLIER_LOGOS.sscMardison,
        publisher: "sc-mardison",
      },
      {
        name: "SIBS Publishing",
        Icon: BookMarked,
        logoSrc: SUPPLIER_LOGOS.sibsPublishing,
        publisher: "sibs-publishing-house",
      },
      {
        name: "Tahanan",
        Icon: BookOpen,
        logoSrc: SUPPLIER_LOGOS.tahanan,
        publisher: "tahanan-books",
      },
    ],
  },
] as const;

function PartnerBubble({ bubble }: { bubble: LocalBubble }) {
  const dark = bubble.dark ?? false;
  const text = dark ? "text-white" : "text-lp-green-dk";
  const bubbleClass = `flex aspect-square size-16 sm:size-20 md:size-28 lg:size-36 shrink-0 flex-col items-center justify-center overflow-hidden rounded-full border-[3px] md:border-[4px] lg:border-[5px] border-[#ABD79E] no-underline transition-all duration-700 ease-in-out hover:border-[#67815F] bg-white`;
  const inner = bubble.logoSrc ? (
    <img
      src={bubble.logoSrc}
      alt={bubble.name}
      loading="lazy"
      decoding="async"
      className="h-full w-full object-contain rounded-full"
    />
  ) : (
    <>
      <bubble.Icon className={`size-5 md:size-7 ${text}`} strokeWidth={2} />
      <span
        className={`px-1 text-center font-bold font-display text-[10px] sm:text-[11px] leading-tight md:text-sm ${text}`}
      >
        {bubble.name}
      </span>
    </>
  );
  return (
    <Link
      to={ROUTES.shop}
      search={{ publisher: [bubble.publisher], page: 1, sort: "default" }}
      aria-label={`Shop ${bubble.name}`}
      className={bubbleClass}
    >
      {inner}
    </Link>
  );
}

function PillRow({
  anchor,
  bubbles,
  revealed,
}: {
  anchor: RowAnchor;
  bubbles: ReadonlyArray<LocalBubble>;
  revealed: boolean;
}) {
  const offset = anchor === "right" ? "100%" : "-100%";
  const transform = revealed ? "translateX(0)" : `translateX(${offset})`;
  const isRight = anchor === "right";
  return (
    <div
      className="w-full transition-transform duration-[1000ms] ease-[cubic-bezier(0.34,1.56,0.64,1)]"
      style={{ transform }}
    >
      <div className="relative mx-auto w-full max-w-[1080px] px-6 md:px-10">
        <div
          className={`absolute inset-y-0 bg-[#D3E9CC] -z-10 ${
            isRight
              ? "left-0 right-[-100vw] rounded-l-full"
              : "right-0 left-[-100vw] rounded-r-full"
          }`}
        />

        <div className="grid grid-cols-4 justify-items-center py-4 sm:py-6 md:py-8 lg:py-10">
          {bubbles.map((bubble) => (
            <PartnerBubble key={bubble.name} bubble={bubble} />
          ))}
        </div>
      </div>
    </div>
  );
}

export function LocalPartners() {
  const { ref, revealed } = useReveal<HTMLDivElement>();

  return (
    <section className="pt-12 pb-24 w-full overflow-hidden">
      {/* <div> */}
        <div className="mx-auto w-full max-w-6xl px-6 md:px-10">
          <div className="border-b-2 border-[#89AC7E]"></div>
    
         <div className="flex flex-col items-center">
          <p className="m-0 mb-2 text-center font-display font-medium text-2xl sm:text-3xl md:text-4xl uppercase leading-tight md:leading-none text-[#67815F] py-8">
            Local Partners
          </p>
        </div>
      </div>
        <div className="mx-auto w-full max-w-7xl px-6 md:px-10">
        <div className="border-b-2 border-[#89AC7E]"></div>
      </div>
      <div
        ref={ref}
        // className="mt-12 flex flex-col gap-8 overflow-hidden md:gap-12"
        className="mt-8 md:mt-12 flex flex-col gap-4 md:gap-6"
      >
        {LOCAL_PARTNER_ROWS.map((row, idx) => (
          <PillRow
            key={`row-${idx}-${row.anchor}`}
            anchor={row.anchor}
            bubbles={row.bubbles}
            revealed={revealed}
          />
        ))}
      </div>
    </section>
  );
}
